"use client";

import { c as _c } from "react/compiler-runtime";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import CreateWaveVotingRep from "./CreateWaveVotingRep";
import CreateWaveVotingThreshold from "./CreateWaveVotingThreshold";
import MemeCardSetPicker from "./MemeCardSetPicker";
import MaxVotesPerIdentityInput from "./MaxVotesPerIdentityInput";
import NegativeVotingToggle from "./NegativeVotingToggle";
import TimeWeightedVoting from "./TimeWeightedVoting";
import type { TimeWeightedVotingConfig } from "./types";
const VOTING_TYPES_ORDER: Record<ApiWaveCreditType, number | undefined> = {
  [ApiWaveCreditType.TdhPlusXtdh]: 0,
  [ApiWaveCreditType.Tdh]: 1,
  [ApiWaveCreditType.Rep]: 2,
  [ApiWaveCreditType.CardSetTdh]: 3,
  [ApiWaveCreditType.Xtdh]: undefined
};
const TIME_WEIGHTED_DURATION_ERROR = "This interval is longer than the wave duration. Choose a shorter interval, extend the wave end date, or clear the end date.";
const VOTING_SETTINGS_GRID_CLASSES = "tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-3 tw-border-t tw-border-iron-700 tw-pt-6";
const VOTING_OPTIONS_GRID_CLASSES = "tw-mt-3 tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-4 [&>div]:tw-gap-x-2 [&>div]:tw-px-3 [&>div]:tw-py-3";
const getCreateWaveVotingLabel = (votingType: ApiWaveCreditType): string => {
  if (votingType === ApiWaveCreditType.CardSetTdh) {
    return "Memes TDH";
  }
  return WAVE_VOTING_LABELS[votingType];
};
export default function CreateWaveVoting(t0) {
  const $ = _c(51);
  const {
    waveType,
    selectedType,
    category,
    profileId,
    creditNfts,
    memeCount,
    isMemeCountLoading,
    isMemeCountError,
    allowNegativeVotes,
    maxVotesPerIdentityPerDrop,
    approvalThreshold,
    errors,
    onTypeChange,
    setCategory,
    setProfileId,
    setCreditNfts,
    onAllowNegativeVotesChange,
    setMaxVotesPerIdentityPerDrop,
    setApprovalThreshold,
    timeWeighted,
    onTimeWeightedChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiWaveType.Chat]: "How Drops are Rated",
      [ApiWaveType.Rank]: "How Drops are Voted",
      [ApiWaveType.Approve]: "How Drops are Voted"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const TITLES = t1;
  if (selectedType === null) {
    return null;
  }
  const timeWeightedErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.TIME_WEIGHTED_VOTING_INTERVAL_EXCEEDS_WAVE_DURATION) ? TIME_WEIGHTED_DURATION_ERROR : undefined;
  let t2;
  if ($[1] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_REQUIRED);
    $[1] = errors;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const approvalThresholdError = t2;
  const showVotingSettings = waveType !== ApiWaveType.Chat;
  const t3 = TITLES[waveType];
  let t4;
  if ($[3] !== t3) {
    t4 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t3}</p>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onTypeChange || $[6] !== selectedType) {
    t5 = (Object.keys(VOTING_TYPES_ORDER) as ApiWaveCreditType[]).filter(_temp).map(votingType_0 => <CommonBorderedRadioButton key={votingType_0} type={votingType_0} selected={selectedType} disabled={false} variant="subtle" onChange={onTypeChange}><span className={`tw-flex tw-min-h-4 tw-items-center tw-text-sm tw-font-semibold ${selectedType === votingType_0 ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white"}`}>{getCreateWaveVotingLabel(votingType_0)}</span></CommonBorderedRadioButton>);
    $[5] = onTypeChange;
    $[6] = selectedType;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== category || $[9] !== errors || $[10] !== profileId || $[11] !== selectedType || $[12] !== setCategory || $[13] !== setProfileId) {
    t6 = selectedType === ApiWaveCreditType.Rep && <div className="tw-col-span-full"><CreateWaveVotingRep category={category} profileId={profileId} errors={errors} setCategory={setCategory} setProfileId={setProfileId} /></div>;
    $[8] = category;
    $[9] = errors;
    $[10] = profileId;
    $[11] = selectedType;
    $[12] = setCategory;
    $[13] = setProfileId;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== creditNfts || $[16] !== errors || $[17] !== isMemeCountError || $[18] !== isMemeCountLoading || $[19] !== memeCount || $[20] !== selectedType || $[21] !== setCreditNfts) {
    t7 = selectedType === ApiWaveCreditType.CardSetTdh && <MemeCardSetPicker creditNfts={creditNfts} memeCount={memeCount} isMemeCountLoading={isMemeCountLoading} isMemeCountError={isMemeCountError} errors={errors} onCreditNftsChange={setCreditNfts} />;
    $[15] = creditNfts;
    $[16] = errors;
    $[17] = isMemeCountError;
    $[18] = isMemeCountLoading;
    $[19] = memeCount;
    $[20] = selectedType;
    $[21] = setCreditNfts;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <div className={VOTING_OPTIONS_GRID_CLASSES}>{t5}{t6}{t7}</div>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== approvalThreshold || $[28] !== approvalThresholdError || $[29] !== errors || $[30] !== maxVotesPerIdentityPerDrop || $[31] !== setApprovalThreshold || $[32] !== setMaxVotesPerIdentityPerDrop || $[33] !== showVotingSettings || $[34] !== waveType) {
    t9 = showVotingSettings && <div data-testid="create-wave-voting-settings-grid" className={VOTING_SETTINGS_GRID_CLASSES}><MaxVotesPerIdentityInput value={maxVotesPerIdentityPerDrop} errors={errors} onChange={setMaxVotesPerIdentityPerDrop} />{waveType === ApiWaveType.Approve && <CreateWaveVotingThreshold threshold={approvalThreshold} error={approvalThresholdError} setThreshold={setApprovalThreshold} />}</div>;
    $[27] = approvalThreshold;
    $[28] = approvalThresholdError;
    $[29] = errors;
    $[30] = maxVotesPerIdentityPerDrop;
    $[31] = setApprovalThreshold;
    $[32] = setMaxVotesPerIdentityPerDrop;
    $[33] = showVotingSettings;
    $[34] = waveType;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== allowNegativeVotes || $[37] !== onAllowNegativeVotesChange || $[38] !== waveType) {
    t10 = waveType !== ApiWaveType.Chat && <NegativeVotingToggle allowNegativeVotes={allowNegativeVotes} onChange={onAllowNegativeVotesChange} isDisabled={false} />;
    $[36] = allowNegativeVotes;
    $[37] = onAllowNegativeVotesChange;
    $[38] = waveType;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== onTimeWeightedChange || $[41] !== timeWeighted || $[42] !== timeWeightedErrorMessage || $[43] !== waveType) {
    t11 = waveType !== ApiWaveType.Chat && <TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} />;
    $[40] = onTimeWeightedChange;
    $[41] = timeWeighted;
    $[42] = timeWeightedErrorMessage;
    $[43] = waveType;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t4 || $[48] !== t8 || $[49] !== t9) {
    t12 = <div>{t4}{t8}{t9}{t10}{t11}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t4;
    $[48] = t8;
    $[49] = t9;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  return t12;
}
function _temp(votingType) {
  return VOTING_TYPES_ORDER[votingType] !== undefined;
}